import React, { FC, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSpinResult, nextStep } from '../../action/SpinToWinAction';
import { RootState } from '../../reducers/RootReducer';
import { SPIN_WHEELS } from '../../constant/spinWheels';
import SpinHeader from '../SpinHeader';
import Button from '../Button/Button';
import DialogBox from '../DialogBox';
import SpinWheel from '../SpinWheel';
import classes from './SpinTarget.module.css';

const SPIN_MS   = 6000;
const SPIN_REVS = 3;
const SETTLE_MS = 400;

const fmt = (n: number) => '$' + n.toLocaleString('en-US');

const SpinTarget: FC<{ isActive?: boolean }> = ({ isActive }) => {
  const dispatch = useDispatch();
  const targetPrice = useSelector((state: RootState) => state.spinToWin.targetPrice);
  // Same index SpinWheel renders, so the price scored always belongs to the face shown
  const wheelIndex = useSelector((state: RootState) => state.spinToWin.wheelIndex);
  const wheel = SPIN_WHEELS[wheelIndex];
  const [ rotation, setRotation ] = useState(0);
  const [ spinning, setSpinning ] = useState(false);
  const [ spun, setSpun ] = useState(false);
  const [ showResult, setShowResult ] = useState(false);

  const handleSpin = useCallback(() => {
    if (spinning || spun) return;

    const segCount = wheel.prices.length;
    const segDeg = 360 / segCount;
    const winIndex = Math.floor(Math.random() * segCount);
    const targetDeg = 360 * SPIN_REVS + ((segCount - winIndex) % segCount) * segDeg;

    setRotation(prev => prev + targetDeg);
    setSpinning(true);

    const spinTimer = setTimeout(() => {
      setSpinning(false);
      setSpun(true);
      dispatch(setSpinResult(wheel.prices[winIndex]));

      const settleTimer = setTimeout(() => setShowResult(true), SETTLE_MS);
      return () => clearTimeout(settleTimer);
    }, SPIN_MS);

    return () => clearTimeout(spinTimer);
  }, [ spinning, spun, dispatch, wheel ]);

  // start_time is stamped by useTimer when the 60s countdown actually begins
  const handleStartChallenge = useCallback(() => {
    dispatch(nextStep());
  }, [ dispatch ]);

  return (
    <div className={`${classes.root} gsap-st-root`}>
      <div className={`${classes.headerContainer} gsap-st-fade`}>
        <SpinHeader/>
      </div>

      <h1 className={`${classes.title} gsap-st-fade`}>SPIN FOR YOUR TARGET</h1>

      <div className={`${classes.wheelWrap} gsap-st-wheel`}>
        <SpinWheel rotation={rotation} spinning={spinning} spinMs={SPIN_MS} />
      </div>

      <Button
        type="button"
        className={`${classes.btn} gsap-st-fade`}
        onClick={handleSpin}
        isDisabled={spinning || spun}
      >
        {spinning ? 'Spinning…' : spun ? 'Done!' : 'Spin the wheel'}
      </Button>

      <DialogBox
        isOpen={showResult}
        closeModal={() => {
        }}
        title=""
        isIcon={false}
        className={classes.dialog}
        containerClassName={classes.dialogContainer}
        panelClassName={classes.dialogPanel}
      >
        <div className={classes.resultCard}>
          <p className={classes.resultLabel}>YOUR TARGET PRICE</p>
          <p className={classes.resultPrice}>{fmt(targetPrice)}</p>
          <p className={classes.resultDesc}>
            Build a ring close to{' '}
            <strong>{fmt(targetPrice)}</strong> as you can. You have{' '}
            <strong>60 seconds</strong> once the builder opens.
          </p>
          <Button
            type="button"
            className={classes.startBtn}
            onClick={handleStartChallenge}
          >
            Start the challenge
          </Button>
        </div>
      </DialogBox>
    </div>
  );
};

export default SpinTarget;
